import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { cn, Icon, Tooltip } from '@prenuvo/halo-foundation';
import { ChevronDown } from '@prenuvo/halo-icon';

import { useFormStructure, useStudyGuard } from '@/shared/guard';
import { OrganLink, OrganLinkProgress } from '@/shared/model';
import { useOrganValidationStore } from '@/store';

import { usePreviewScrollPosition } from '../../../hook';
import { generateReportingRoute } from '../../../util';
import {
  DEFAULT_EXPANDED_LINK_KEYS,
  NOT_AN_ORGAN,
  SKU_WITHOUT_NECK_ORGAN,
} from './nav-menu.constant';
import { getStatusContent } from './nav-menu.util';
import { useNavMenu } from './nav-menu-context';
import { ProgressIndicator } from './progress-indicator';
import { useKiLabelCount } from './use-ki-label-count.hook';
import { useUpdateSubOrganStatus } from './use-update-sub-organ-status.hook';

interface NavMenuLinkProps {
  index: number;
  isSubmenuItem: boolean;
  link: OrganLink;
  onClick?: () => void;
}

export function NavMenuLink({ index, isSubmenuItem = false, link, onClick }: NavMenuLinkProps) {
  const {
    currentOrgan,
    hoverTimeoutRef,
    isSubmenuHovered,
    linkItemRefs,
    onHoveredMenuChange,
    onLinkSelect,
    onOrganSelect,
    selectedOrgan,
  } = useNavMenu();
  const {
    radiologistScan: { skuID },
    studyId,
  } = useStudyGuard();

  const { getStatus, setInCompleteOrgan } = useOrganValidationStore();
  const { links } = useFormStructure();
  const setPreviewScrollPosition = usePreviewScrollPosition();
  const getKeyImageLabelCount = useKiLabelCount();
  const [expandedMenus, setExpandedMenus] = useState<string[]>(
    SKU_WITHOUT_NECK_ORGAN.includes(skuID) ? [] : DEFAULT_EXPANDED_LINK_KEYS,
  );

  const isActive = link.key === selectedOrgan || link.sub?.some((sub) => sub.key === selectedOrgan);
  const isDisabled = false;
  const hasSubmenu = link.sub && link.sub.length > 0;
  const isExpanded = expandedMenus.includes(link.key);

  const organLink = links.find((linkData) => linkData.key === link.key);
  const subOrganLink = organLink?.sub ? organLink.sub.map((item) => item.key) : null;

  const organStatus = getStatus(link.key);

  useUpdateSubOrganStatus(link.key, subOrganLink);

  const keyImageLabelCount = getKeyImageLabelCount(link.key);

  const toggleSubmenu = (key: string) => {
    setExpandedMenus((prev) =>
      prev.includes(key) ? prev.filter((k) => k !== key) : [...prev, key],
    );
  };

  const hasSubmenuStatus = () =>
    link.sub?.some((subLink) => getStatus(subLink.key)?.error || getStatus(subLink.key)?.warning);

  const hasSubmenuError = () => link.sub?.some((subLink) => getStatus(subLink.key)?.error);

  const hasSubmenuWarning = () => link.sub?.some((subLink) => getStatus(subLink.key)?.warning);

  const shouldShowIndicator = (linkKey: string) => {
    const status = getStatus(linkKey);
    const isCurrentPage = currentOrgan && linkKey === currentOrgan;

    if (status?.warning || (hasSubmenu && hasSubmenuStatus())) {
      return true;
    }

    return !isCurrentPage && (status?.error || (hasSubmenu && hasSubmenuError()));
  };

  const getIndicatorProgress = (linkKey: string): null | OrganLinkProgress => {
    const status = getStatus(linkKey);
    const isCurrentPage = currentOrgan && linkKey === currentOrgan;

    if (!isCurrentPage && (status?.error || (hasSubmenu && hasSubmenuError()))) {
      return 'missingInput';
    }

    if (!status?.error && (status?.warning || (hasSubmenu && hasSubmenuWarning()))) {
      return 'oncorad';
    }

    return null;
  };

  useEffect(() => {
    if (getIndicatorProgress(link.key) === 'missingInput') {
      setInCompleteOrgan(link.key, true);
    }
  }, [link.key, getIndicatorProgress(link.key)]);

  return (
    <div
      className="relative w-full overflow-hidden"
      onMouseEnter={() => {
        clearTimeout(hoverTimeoutRef.current);
        onHoveredMenuChange(link.key);
      }}
      onMouseLeave={() => {
        hoverTimeoutRef.current = setTimeout(() => {
          if (!isSubmenuHovered) {
            onHoveredMenuChange(null);
          }
        }, 500);
      }}
    >
      <Link
        className={cn(
          'group inline-flex items-center justify-center whitespace-nowrap text-sm font-medium text-neutral-400 dark:text-neutral-400 hover:no-underline w-full',
          {
            'dark:bg-stone-800': isActive && isSubmenuItem,
            'hover:bg-stone-800': isSubmenuItem,
            'text-neutral-800 dark:text-neutral-800': isDisabled || link.disabled,
          },
        )}
        data-testid="nav-menu-link"
        onClick={(e) => {
          if (hasSubmenu) {
            e.preventDefault();
            toggleSubmenu(link.key);
          } else {
            setPreviewScrollPosition();
            onOrganSelect(link.key);
            onLinkSelect?.(link.key);
            onClick?.();
          }
        }}
        to={generateReportingRoute(studyId, link.key)}
      >
        <Tooltip
          className={cn(
            'bg-stone-700 text-xs text-neutral-50 dark:bg-stone-700 dark:text-neutral-50',
            {
              hidden:
                (getStatusContent(getStatus(link.key)) === '' && !link.disabled) || hasSubmenu,
            },
          )}
          content={`${link.title} ${link.disabled ? '- Not in SKU' : getStatusContent(getStatus(link.key))}`}
          side="right"
        >
          <div
            className={cn(
              'flex w-full justify-between static hover:bg-stone-800 dark:hover:bg-stone-800 h-full items-center hover:text-neutral-400 dark:hover:text-neutral-400',
              {
                'bg-stone-800 dark:bg-stone-800 text-neutral-50 dark:text-neutral-50 hover:text-neutral-50 dark:hover:text-neutral-50':
                  isActive && (!hasSubmenu || !isExpanded || isExpanded),
                'flex items-center justify-center': link.key === 'preview',
                'text-neutral-50 dark:text-neutral-50 hover:text-neutral-50 dark:hover:text-neutral-50':
                  isActive && hasSubmenu && isExpanded,
                'text-neutral-800 dark:text-neutral-800':
                  isSubmenuItem && link.disabled && !isActive,
              },
            )}
            data-selected={`${isActive}`}
            ref={(el: HTMLDivElement | null) => {
              linkItemRefs.current[index] = el;
            }}
          >
            <span className="relative flex items-center">
              <div
                className={cn('w-1 absolute left-0 top-0 h-full', {
                  'bg-green-500':
                    !NOT_AN_ORGAN.includes(link.key) && organStatus?.done && !organStatus?.error,
                  'bg-neutral-700':
                    !NOT_AN_ORGAN.includes(link.key) && (!organStatus?.done || organStatus?.error),
                })}
              />
              <span className="whitespace-normal py-[2px] pl-3 pr-2 text-sm font-normal no-underline">
                {link.title.charAt(0).toUpperCase() + link.title.slice(1).toLowerCase()}
                {keyImageLabelCount > 0 && <span className="pl-2">{keyImageLabelCount}</span>}
              </span>
            </span>
            <div className="flex items-center">
              {(!hasSubmenu || (hasSubmenu && !isExpanded)) && shouldShowIndicator(link.key) && (
                <span className="ml-auto flex shrink-0 items-center">
                  <ProgressIndicator progress={getIndicatorProgress(link.key)} />
                </span>
              )}
              {hasSubmenu && (
                <Icon
                  className={cn(
                    'h-4 w-4 transition-transform fill-current ml-2',
                    isExpanded && 'rotate-180',
                  )}
                  source={ChevronDown}
                />
              )}
            </div>
          </div>
        </Tooltip>
      </Link>
      {hasSubmenu && isExpanded && (
        <div className="ml-3 flex flex-col gap-1 pt-1">
          {link.sub?.map((subLink, subIndex) => (
            <NavMenuLink
              key={subLink.key}
              index={index + subIndex}
              isSubmenuItem
              link={{
                key: subLink.key,
                disabled: subLink.disabled,
                progress: null,
                title: subLink.value,
              }}
            />
          ))}
        </div>
      )}
    </div>
  );
}
